// Imperative confirm/info dialogs (R16) — native successor to the v3
// UIHelper `Modal.confirm`/`Modal[type]` statics, same call surfaces.
import i18n from 'i18next';
import { ReactNode, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { APConfirmDialog } from '@/components/ap/confirm';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ShowConfirmModalProps {
  title?: ReactNode;
  content?: ReactNode;
  okText?: string;
  cancelText?: string;
  okType?: string;
  onOk?: () => void;
  onCancel?: () => void;
  // v3 pass-throughs kept for call-site compatibility (no-ops here).
  translate?: (key: string) => string;
  icon?: ReactNode;
  className?: string;
  zIndex?: number;
  centered?: boolean;
}

export interface ShowInfoModalProps {
  type?: string; // v3 antd Modal static key (error/info/warning/success) — presentation only
  title?: string;
  content?: ReactNode;
  buttonText?: string;
}

type Unmount = () => void;

const mountEphemeral = (render: (unmount: Unmount) => ReactNode): void => {
  const el = document.createElement('div');
  document.body.appendChild(el);
  const root = createRoot(el);
  const unmount = () => {
    // Let the Radix close animation settle before tearing the root down.
    setTimeout(() => {
      root.unmount();
      el.remove();
    }, 200);
  };
  root.render(render(unmount));
};

const ConfirmDialogHost = ({
  props,
  unmount,
}: {
  props: ShowConfirmModalProps;
  unmount: Unmount;
}) => {
  const [open, setOpen] = useState(true);
  const t = props.translate ?? i18n.t;
  return (
    <APConfirmDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) unmount();
      }}
      title={props.title ?? t('CommonText.are_you_sure')}
      description={props.content || undefined}
      confirmLabel={props.okText ?? t('CommonText.yes')}
      cancelLabel={props.cancelText ?? t('CommonText.no')}
      destructive={(props.okType ?? 'danger') === 'danger'}
      onConfirm={() => props.onOk?.()}
      onCancel={() => props.onCancel?.()}
      data-testid="ap-imperative-confirm"
    />
  );
};

const InfoDialogHost = ({ props, unmount }: { props: ShowInfoModalProps; unmount: Unmount }) => {
  const [open, setOpen] = useState(true);
  const close = () => {
    setOpen(false);
    unmount();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="ap-imperative-info">
        <DialogHeader>
          <DialogTitle>{props.title ?? 'Something went wrong!'}</DialogTitle>
          {props.content ? <DialogDescription>{props.content}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={close} data-testid="ap-imperative-info-ok">
            {props.buttonText ?? 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** v3 UIHelper.showConfirmModal call surface — Yes/No confirm. */
export const showConfirmModal = (props: ShowConfirmModalProps): void => {
  mountEphemeral((unmount) => <ConfirmDialogHost props={props} unmount={unmount} />);
};

export interface ShowBlockingDialogProps {
  title: ReactNode;
  content?: ReactNode;
  /** The only affordances — the dialog itself cannot be dismissed. */
  footer: ReactNode;
}

/** Non-dismissable alert with a caller-owned footer (lockdown quit alerts). */
export const showBlockingDialog = (props: ShowBlockingDialogProps): void => {
  mountEphemeral(() => (
    <Dialog open>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="ap-imperative-blocking"
      >
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          {props.content ? <DialogDescription>{props.content}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter>{props.footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  ));
};

/** v3 UIHelper.showInfoModal call surface — single-OK notice. */
export const showInfoModal = (props: ShowInfoModalProps): void => {
  mountEphemeral((unmount) => <InfoDialogHost props={props} unmount={unmount} />);
};
